import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setErr("No token"); return; }
    (async () => {
      try {
        const { data } = await api.get(`/workspace/invite/${token}`);
        setInvite(data);
        setForm((f) => ({ ...f, name: data.name || data.email.split("@")[0] }));
      } catch (e) {
        setErr(e?.response?.data?.detail || "Invalid invite");
      }
    })();
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/workspace/invite/accept", {
        token,
        password: form.password,
        name: form.name,
      });
      localStorage.setItem("token", data.token);
      setUser(data.user);
      toast.success(`Welcome to ${invite.workspace_name}`);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accept failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center p-6" style={{ fontFamily: "Manrope" }}>
      <div className="w-full max-w-sm">
        {err && <div className="bg-red-500/10 border border-red-500/30 text-red-700 rounded p-4 text-sm">{err}</div>}
        {invite && (
          <>
            <div className="mb-5">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">You're invited</div>
              <h1 className="text-2xl font-semibold tracking-tight mt-1">Join {invite.workspace_name}</h1>
              <p className="text-sm text-slate-500 mt-2">as <b className="text-slate-900">{invite.role}</b> · <span className="font-mono">{invite.email}</span></p>
            </div>
            <form onSubmit={accept} className="space-y-3">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                data-testid="accept-name"
                className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
              <input
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Set a password (6+ chars)"
                data-testid="accept-password"
                className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
              <button
                disabled={busy}
                data-testid="accept-submit"
                className="w-full bg-blue-600 text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
              >
                {busy ? "…" : "Accept & create account"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
